// ═════════════════════════════════════════════════════════════════════════════
// Mack · BACKTEST tab (build-07) — THE SAME CHAIN, ON A PAST DATE
//
// SOURCE: scratchpad/mack_design_refs/build-07_tab-08_backtest.png
// Colours INHERIT build-01 tokens + C.teal. Nothing new.
//
// NOT A SEPARATE SYSTEM, and it must never become one. If the backtest path and
// the live path can diverge, the backtest measures a framework that is not the
// one being used. Capacity here runs through Flow's own computeCapacity and the
// phase column through the shared classifyStage — the same functions the live
// tabs call, parameterised by the landing date instead of today.
//
// THE MECHANISM: a landing date is selected, every input is reconstructed AS IT
// STOOD THAT DAY (as-released, never revised), the chain runs, and the
// attribution COMMITS — ranked tiers, discriminator, speed class, review point —
// BEFORE any outcome is visible.
//
// ⚠ THE OUTCOME IS SEALED BY DEFAULT, and blinding is enforced by the interface
// rather than by discipline. Knowing what happened, it is very easy to
// unconsciously select the tier that fits. Changing episode or instrument
// RE-SEALS, because landing on a fresh read with its outcome already showing
// would defeat the whole mechanism.
//
// WHAT IS BEING TESTED — three claims, none of them price prediction: did the
// named tier hold; did persistence match the declared speed class (a read with
// the right direction and the wrong persistence profile is a FAILURE, and the
// more informative of the two); did the discriminator resolve as written.
//
// TWO ACCURACIES, COUNTED SEPARATELY. Attribution accuracy accrues at the rate
// reads are made and builds quickly. Classifier accuracy — whether the stage
// itself was right — accrues only as regimes change and builds very slowly.
// Reading them as one number understates the first and overstates the second.
//
// AND ONLY NEW ATTRIBUTIONS COUNT toward the hit rate. A read that persists
// across an unchanged cycle is one call re-confirmed, not several.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from 'react';
import { C, MONO } from './tokens';
import { card, panelTitle, panelNote, outlinePill, microLabel, Chip } from './primitives';
import { computeCapacity } from './FlowTab';
import { classifyStage } from './stages';
import { MackBlockKey, blockBandThirds } from './blocks';

// ── TYPES ────────────────────────────────────────────────────────────────────

// The third state is what protects the whole exercise: a figure the fleet cannot
// recover as-released is emitted ABSENT, never backfilled from the current series.
export type MackVintageFlag = 'as-released' | 'non-revising' | 'absent';

export interface MackReconstructedVar {
  variable: string;
  value: string;
  source: string;
  pulled_at: string;
  vintage_flag: MackVintageFlag;
}

export interface MackSheetStateThen {
  sheet: string;
  delta_growth: string;
  delta_inflation: string;
  blocks: Record<string, number>;   // the design shows four, not six
  activation: number;
  reactive: boolean;
  // NO `phase` FIELD — derived through the shared classifyStage, same as Phases.
}

export interface MackCapacityThen {
  instrument: string;
  price_then: string;
  sigma_then: number;
  target_move: number;   // the declared target at commit — see the note below
  // NO ceiling / band / R FIELDS. All three come from Flow's computeCapacity.
}

export interface MackCommittedRead {
  tier: string;
  sheet: string;
  confidence: string;
  committed_on: string;
  // NO capacity_line FIELD. The band half of that line is Flow's computed band;
  // storing the whole string let my own extrapolated USD/JPY read print 'Band 2'
  // beside a computed Band 3. Only the dominant block is stored; the line is composed.
  dominant_block: string;
  ranked: { tier: string; pct: number }[];
  discriminator: string;
  speed: string;
  review_point: string;
  implied_side: string;
  kind: 'new' | 'continuation';
  cycle_line: string;
}

export interface MackOutcome {
  price_at_horizon: string;
  move: string;
  ran: string;
  attribution_held: boolean;
  direction_held: boolean;
  note: string;
}

export interface MackCheck { claim: string; claimed: string; actual: string; result: 'hit' | 'miss' }

export interface MackTallyRow {
  episode: string; instrument: string; attribution: string;
  kind: 'new' | 'cont'; ran: string;
  held: boolean; direction_held: boolean;
}

export interface MackBacktestEpisode {
  id: string;
  label: string;
  landing_date: string;
  reconstructed: MackReconstructedVar[];
  sheet_state: MackSheetStateThen[];
  reads: {
    instrument: string;
    capacity_then: MackCapacityThen;
    committed: MackCommittedRead;
    outcome: MackOutcome;
    checks: MackCheck[];
    from_design: boolean;
  }[];
  from_design: boolean;
}

// ── BAR SCALE ────────────────────────────────────────────────────────────────
// Measured off THIS design: a 38px track, 34% row filling 34px, 18% filling 18px,
// 12% filling 12px — a tight fill/pct ratio of 2.63, so the track represents
// ≈0.38 and fill stays proportional to each row's own share.
//
// NOTE FOR THE RECORD: Cascade's design measured 0.35 on the same control (51px
// track, 23% -> 33px). The two tabs' rank bars therefore use slightly different
// ceilings because their designs do. Flagged as a unification candidate; not
// forced here, since Cascade is cleared and each tab matches its own image.
const RANK_BAR_CEILING = 0.38;

// A is banded the same way Phases bands M — ≥0.80 green, ≥0.40 amber, below that
// muted. Verified against every A the design prints: 0.94/0.88 green, 0.55/0.42
// amber, 0.30 muted.
function activationBand(v: number): string {
  if (v >= 0.80) return C.green;
  if (v >= 0.40) return C.amber;
  return C.textMuted;
}

// Thirds banding, from blocks.ts. Verified against all twenty values the design
// prints on this tab.
const blockBand = (v: number) => blockBandThirds(v, C.green, C.amber, C.red);

const PHASE_COLOUR: Record<string, string> = {
  Reflation: C.green, Stagflation: C.red, Expansion: C.textSub,
  Deflation: C.red, '—': C.textMuted,
};

// The design's sheet-state table shows FOUR block columns, not the six Phases
// carries. Transcribed, not normalised.
const STATE_BLOCKS: MackBlockKey[] = ['Policy', 'Prices', 'Growth', 'External'];

// ── PLACEHOLDER DATA ─────────────────────────────────────────────────────────
// The 13 October 2025 episode is TRANSCRIBED VERBATIM — all twelve reconstructed
// variables with their sources and archive-pull notes, all five sheet states,
// the capacity row, the committed read with its ranked tiers, the five
// verification claims and the whole tally.
//
// THE OUTCOME IS NOT IN THE DESIGN — the design ships sealed, which is the point.
// The revealed content below is therefore WRITTEN, not transcribed. FLAGGED.
//
// TARGET MOVE: the design shows Band 3 / R 0.35 for XAU/USD at σ 1.74%. Flow's
// formula puts ceiling₁ at 0.385%, and Band 3 needs multiple > 400, i.e. a target
// above 7.70%. The design does not print the target, so 8.0% is back-solved from
// the band it shows — the constraint is exact, the value inside it is not.
export const MACK_BACKTEST_EPISODES: MackBacktestEpisode[] = [
  {
    id: 'ep_2025_10_13', label: 'Week of 13 October 2025', landing_date: '13 October 2025',
    from_design: true,
    reconstructed: [
      { variable: 'Gold spot',     value: '$4,062', source: 'Exchange',      pulled_at: 'archive pull · 13 Oct 25',            vintage_flag: 'as-released' },
      { variable: 'Fed Funds',     value: '4.00%',  source: 'FRED',          pulled_at: 'archive pull · vintage 13 Oct',       vintage_flag: 'as-released' },
      { variable: 'US CPI Y/Y',    value: '3.0%',   source: 'FRED',          pulled_at: 'archive pull · Sep print, as-released', vintage_flag: 'as-released' },
      { variable: 'US GDPNow',     value: '+2.4%',  source: 'Atlanta Fed',   pulled_at: 'archive pull · 13 Oct vintage',       vintage_flag: 'as-released' },
      { variable: 'US 10Y',        value: '4.04%',  source: 'FRED',          pulled_at: 'archive pull · 13 Oct',               vintage_flag: 'non-revising' },
      { variable: '2Y real (US)',  value: '+1.42%', source: 'FRED',          pulled_at: 'archive pull · 13 Oct',               vintage_flag: 'non-revising' },
      { variable: 'VIX',           value: '21.7',   source: 'Exchange',      pulled_at: 'archive pull · 13 Oct',               vintage_flag: 'non-revising' },
      { variable: 'Net liquidity', value: '$6.11T', source: 'WALCL-TGA-RRP', pulled_at: 'archive pull · 10 Oct',               vintage_flag: 'as-released' },
      { variable: 'JP CPI Y/Y',    value: '2.9%',   source: 'Stats Bureau',  pulled_at: 'archive pull · Sep print',            vintage_flag: 'as-released' },
      { variable: 'BoJ rate',      value: '0.50%',  source: 'BoJ',           pulled_at: 'archive pull · 13 Oct',               vintage_flag: 'non-revising' },
      { variable: 'UK CPI Y/Y',    value: '3.8%',   source: 'ONS',           pulled_at: 'archive pull · Sep print',            vintage_flag: 'as-released' },
      { variable: 'XAU Σ daily',   value: '1.74%',  source: 'Exchange',      pulled_at: 'archive pull · 30d realised',         vintage_flag: 'non-revising' },
    ],
    sheet_state: [
      { sheet: 'US',           delta_growth: 'rising',  delta_inflation: 'stable',  blocks: { Policy: 0.72, Prices: 0.50, Growth: 0.85, External: 0.55 }, activation: 0.94, reactive: true },
      { sheet: 'Reserve bloc', delta_growth: 'stable',  delta_inflation: 'stable',  blocks: { Policy: 0.60, Prices: 0.50, Growth: 0.20, External: 0.92 }, activation: 0.88, reactive: true },
      { sheet: 'JPY',          delta_growth: 'rising',  delta_inflation: 'rising',  blocks: { Policy: 0.28, Prices: 0.50, Growth: 0.50, External: 0.88 }, activation: 0.42, reactive: false },
      { sheet: 'UK',           delta_growth: 'falling', delta_inflation: 'rising',  blocks: { Policy: 0.68, Prices: 1.00, Growth: 0.10, External: 0.22 }, activation: 0.55, reactive: false },
      { sheet: 'EUR',          delta_growth: 'rising',  delta_inflation: 'rising',  blocks: { Policy: 0.34, Prices: 0.50, Growth: 0.45, External: 0.72 }, activation: 0.30, reactive: false },
    ],
    reads: [
      {
        instrument: 'XAU/USD', from_design: true,
        capacity_then: { instrument: 'XAU/USD', price_then: '$4,062', sigma_then: 1.74, target_move: 8.0 },
        committed: {
          tier: 'Central Banks (non-Western)', sheet: 'Reserve bloc',
          confidence: 'High', committed_on: '13 October 2025',
          dominant_block: 'External',
          ranked: [
            { tier: 'Central Banks (non-Western)', pct: 0.34 },
            { tier: 'Sovereign Wealth',            pct: 0.26 },
            { tier: 'Macro Funds',                 pct: 0.18 },
            { tier: 'Institutional',               pct: 0.12 },
            { tier: 'CTAs',                        pct: 0.06 },
          ],
          discriminator:
            'Value migration. Control building higher over the following fortnight confirms ' +
            'official-sector accumulation; a push that rejects back below the prior value area ' +
            'means it was macro-fund positioning and the attribution moves down one tier.',
          speed: 'Slow · structural', review_point: '4–8 weeks', implied_side: 'Long gold',
          kind: 'continuation', cycle_line: 'Reserve accumulation · 41w · intact',
        },
        outcome: {
          price_at_horizon: '$4,318', move: '+6.3%', ran: '7 weeks',
          attribution_held: true, direction_held: true,
          note: 'Control migrated higher through the fortnight and held above the prior value area — ' +
                'the discriminator resolved to official-sector accumulation as written. Persistence ' +
                'matched the declared slow-structural profile: a grind, not a spike.',
        },
        checks: [
          { claim: 'Direction over 4–8 weeks',        claimed: 'Long gold',              actual: 'Held long, +6.3% over 7 weeks',      result: 'hit' },
          { claim: 'Persistence profile',             claimed: 'Slow grind, no spike',   actual: 'Grind — no single session over 1.6%', result: 'hit' },
          { claim: 'Discriminator — value migration', claimed: 'Control builds higher',  actual: 'Control migrated higher, fortnight two', result: 'hit' },
          { claim: 'No rejection into prior area',    claimed: 'Holds above prior value', actual: 'Held above throughout',              result: 'hit' },
          { claim: 'Speed class',                     claimed: 'Slow · structural',      actual: 'Ran 7 weeks against a declared 4–8',  result: 'hit' },
        ],
      },
      {
        instrument: 'USD/JPY', from_design: false,
        capacity_then: { instrument: 'USD/JPY', price_then: '¥151.80', sigma_then: 0.62, target_move: 2.5 },
        committed: {
          tier: 'Central Banks', sheet: 'JPY',
          confidence: 'Moderate', committed_on: '13 October 2025',
          dominant_block: 'Policy',
          ranked: [
            { tier: 'Central Banks',    pct: 0.31 },
            { tier: 'Corporates',       pct: 0.24 },
            { tier: 'Large Banks',      pct: 0.19 },
            { tier: 'Macro Funds',      pct: 0.15 },
            { tier: 'Hedge Funds',      pct: 0.11 },
          ],
          discriminator:
            'Intervention signature. A sharp reversal inside a single session with no catalyst ' +
            'confirms official action; a steady drift means it was carry unwind and the attribution ' +
            'moves to Macro Funds.',
          speed: 'Fast', review_point: '1–3 weeks', implied_side: 'Short USD/JPY',
          kind: 'new', cycle_line: 'JPY normalising · 6w · turning',
        },
        outcome: {
          price_at_horizon: '¥155.20', move: '+2.2%', ran: '5 weeks',
          attribution_held: false, direction_held: true,
          note: 'Direction was right but the signature was wrong. No single-session reversal ever ' +
                'appeared — the move drifted, which the discriminator had assigned to carry unwind. ' +
                'The attribution should have moved to Macro Funds at week two and did not.',
        },
        checks: [
          { claim: 'Direction over 1–3 weeks',       claimed: 'Short USD/JPY',        actual: 'Direction held',                    result: 'hit' },
          { claim: 'Persistence profile',            claimed: 'Fast, single impulse', actual: 'Drifted across five weeks',         result: 'miss' },
          { claim: 'Discriminator — intervention',   claimed: 'Sharp session reversal', actual: 'No reversal — steady drift',      result: 'miss' },
          { claim: 'Speed class',                    claimed: 'Fast',                 actual: 'Ran 5 weeks against a declared 1–3', result: 'miss' },
        ],
      },
    ],
  },

  // EXTRAPOLATED — the design shows the October episode in full and names these
  // two only in the tally.
  {
    id: 'ep_2025_07_21', label: 'Week of 21 July 2025', landing_date: '21 July 2025',
    from_design: false,
    reconstructed: [
      { variable: 'Gold spot',    value: '$3,338', source: 'Exchange',    pulled_at: 'archive pull · 21 Jul 25',   vintage_flag: 'as-released' },
      { variable: 'Fed Funds',    value: '4.50%',  source: 'FRED',        pulled_at: 'archive pull · vintage 21 Jul', vintage_flag: 'as-released' },
      { variable: 'US CPI Y/Y',   value: '2.7%',   source: 'FRED',        pulled_at: 'archive pull · Jun print',   vintage_flag: 'as-released' },
      { variable: 'US GDPNow',    value: '+2.4%',  source: 'Atlanta Fed', pulled_at: 'archive pull · 21 Jul vintage', vintage_flag: 'as-released' },
      { variable: 'US 10Y',       value: '4.38%',  source: 'FRED',        pulled_at: 'archive pull · 21 Jul',      vintage_flag: 'non-revising' },
      { variable: 'VIX',          value: '16.4',   source: 'Exchange',    pulled_at: 'archive pull · 21 Jul',      vintage_flag: 'non-revising' },
      { variable: 'EZ HICP Y/Y',  value: '2.0%',   source: 'Eurostat',    pulled_at: 'archive pull · Jun print',   vintage_flag: 'as-released' },
      { variable: 'DAX Σ daily',  value: '1.05%',  source: 'Exchange',    pulled_at: 'archive pull · 30d realised', vintage_flag: 'non-revising' },
      { variable: 'EZ PMI comp',  value: '—',      source: 'S&P GMI',     pulled_at: 'not recoverable as-released', vintage_flag: 'absent' },
    ],
    sheet_state: [
      { sheet: 'US',           delta_growth: 'rising',  delta_inflation: 'stable', blocks: { Policy: 0.80, Prices: 0.50, Growth: 0.90, External: 0.50 }, activation: 0.35, reactive: false },
      { sheet: 'EUR',          delta_growth: 'rising',  delta_inflation: 'rising', blocks: { Policy: 0.30, Prices: 0.50, Growth: 0.55, External: 0.70 }, activation: 0.82, reactive: true },
      { sheet: 'Reserve bloc', delta_growth: 'stable',  delta_inflation: 'stable', blocks: { Policy: 0.55, Prices: 0.50, Growth: 0.20, External: 0.88 }, activation: 0.61, reactive: true },
    ],
    reads: [
      {
        instrument: 'DAX', from_design: false,
        capacity_then: { instrument: 'DAX', price_then: '€24,300', sigma_then: 1.05, target_move: 4.0 },
        committed: {
          tier: 'Institutional', sheet: 'EUR', confidence: 'Moderate', committed_on: '21 July 2025',
          dominant_block: 'Growth',
          ranked: [
            { tier: 'Institutional', pct: 0.29 }, { tier: 'Large Banks', pct: 0.23 },
            { tier: 'Macro Funds', pct: 0.20 }, { tier: 'Hedge Funds', pct: 0.16 },
            { tier: 'Quant Funds', pct: 0.12 },
          ],
          discriminator:
            'Breadth. If the advance holds with breadth this is institutional allocation; if it ' +
            'narrows to a handful of names it was systematic flow and the attribution moves to Quant Funds.',
          speed: 'Moderate', review_point: '3–6 weeks', implied_side: 'Long DAX',
          kind: 'new', cycle_line: 'EUR recovering · 11w · early',
        },
        outcome: {
          price_at_horizon: '€23,610', move: '−2.8%', ran: 'failed at week 2',
          attribution_held: false, direction_held: false,
          note: 'Failed on both counts. Breadth narrowed inside two weeks and the advance reversed — ' +
                'the discriminator had named that outcome and the read did not act on it.',
        },
        checks: [
          { claim: 'Direction over 3–6 weeks', claimed: 'Long DAX',                actual: 'Reversed, −2.8%',            result: 'miss' },
          { claim: 'Persistence profile',      claimed: 'Moderate, steady advance', actual: 'Failed at week two',        result: 'miss' },
          { claim: 'Discriminator — breadth',  claimed: 'Advance holds with breadth', actual: 'Breadth narrowed sharply', result: 'miss' },
          { claim: 'Speed class',              claimed: 'Moderate',                 actual: 'Resolved inside two weeks',  result: 'miss' },
        ],
      },
    ],
  },
];

// Instrument-independent history, transcribed from the design.
export const MACK_BACKTEST_TALLY: MackTallyRow[] = [
  { episode: '13 October 2025', instrument: 'XAU/USD', attribution: 'Central Banks', kind: 'cont', ran: '7 weeks',           held: true,  direction_held: true },
  { episode: '13 October 2025', instrument: 'USD/JPY', attribution: 'Central Banks', kind: 'new',  ran: '5 weeks',           held: false, direction_held: true },
  { episode: '21 July 2025',    instrument: 'DAX',     attribution: 'Institutional', kind: 'new',  ran: 'failed at week 2',  held: false, direction_held: false },
  { episode: '21 July 2025',    instrument: 'XAU/USD', attribution: 'Macro Funds',   kind: 'cont', ran: '4 weeks',           held: true,  direction_held: true },
  { episode: '7 April 2025',    instrument: 'XAU/USD', attribution: 'Central Banks', kind: 'new',  ran: '6 weeks',           held: true,  direction_held: true },
];

// ── GRIDS — measured (content 753px → ×1.965 → 1480 CSS) ─────────────────────
const STATE_GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) 150px repeat(4, 92px) 78px 108px', alignItems: 'center',
};
const CAPACITY_GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 130px 100px 110px 190px 80px', alignItems: 'center',
};
const TALLY_GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'minmax(0,1.15fr) 110px 150px 74px 150px 120px', alignItems: 'center',
};

const monoSmall: React.CSSProperties = { fontSize: 11.5, fontFamily: MONO };

// ── TAB ──────────────────────────────────────────────────────────────────────

export function BacktestTab() {
  const [episodeId, setEpisodeId] = useState(MACK_BACKTEST_EPISODES[0].id);
  const [instrument, setInstrument] = useState(MACK_BACKTEST_EPISODES[0].reads[0].instrument);
  // SEALED BY DEFAULT. Blinding is enforced here, not by discipline.
  const [revealed, setRevealed] = useState(false);

  const episode = useMemo(
    () => MACK_BACKTEST_EPISODES.find(e => e.id === episodeId) ?? MACK_BACKTEST_EPISODES[0],
    [episodeId],
  );
  const read = useMemo(
    () => episode.reads.find(r => r.instrument === instrument) ?? episode.reads[0],
    [episode, instrument],
  );

  // Selecting a new episode or instrument RE-SEALS. Landing on a fresh read with
  // its outcome already visible would defeat the mechanism.
  const chooseEpisode = (id: string) => {
    const next = MACK_BACKTEST_EPISODES.find(e => e.id === id) ?? MACK_BACKTEST_EPISODES[0];
    setEpisodeId(id);
    setInstrument(next.reads[0].instrument);
    setRevealed(false);
  };
  const chooseInstrument = (sym: string) => { setInstrument(sym); setRevealed(false); };

  // Capacity runs through FLOW'S OWN FUNCTION — same code path as the live tab,
  // parameterised by that date's σ and the declared target rather than today's.
  // Passing the baseline VIX leaves σ unscaled, which is correct: σ_then is
  // already the realised volatility for the landing date.
  const cap = useMemo(
    () => computeCapacity(read.capacity_then.sigma_then, 17.4, read.capacity_then.target_move),
    [read],
  );

  const readsRun = MACK_BACKTEST_TALLY.length;
  const independent = MACK_BACKTEST_TALLY.filter(t => t.kind === 'new').length;

  const R = (v: React.ReactNode, s: React.CSSProperties, key?: string) =>
    <span key={key} style={{ ...s, textAlign: 'right' }}>{v}</span>;

  const statePill = (reactive: boolean) => {
    const col = reactive ? C.green : C.textMuted;
    return (
      <span style={{
        fontSize: 9, fontFamily: MONO, letterSpacing: '0.12em', textTransform: 'uppercase',
        border: `1px solid ${col}`, color: col, borderRadius: 20, padding: '3px 9px', whiteSpace: 'nowrap',
      }}>{reactive ? 'Reactive' : 'Sleeping'}</span>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>

      {/* ── HEADER + CONTROLS ────────────────────────────────────────── */}
      <div style={{ ...card, padding: '19px 24px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={panelTitle}>Backtest — the same chain, on a past date</div>
            <div style={{ ...panelNote, maxWidth: 1180, marginTop: 7 }}>
              Land on a historical week. The fleet reconstructs every input as it stood <i>that day</i> —
              as-released, never revised — and the identical chain runs. The attribution commits with
              its discriminator and review point <b style={{ color: C.textSub, fontWeight: 700 }}>before</b> the
              outcome is revealed, so hindsight cannot leak backwards into it. Landing dates should be
              selected at random and the dull weeks logged — testing only weeks where something
              happened measures how well the framework explains events, not how well it navigates
              ordinary time.
            </div>
          </div>
          <span style={{ ...outlinePill(C.violet), flexShrink: 0 }}>Reconstructed · blinded</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22, marginTop: 18, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 320 }}>
            <div style={{ ...microLabel, marginBottom: 9 }}>Episode — landing week</div>
            <div style={{ position: 'relative' }}>
              <select
                value={episodeId} aria-label="Episode landing week"
                onChange={e => chooseEpisode(e.target.value)}
                style={{
                  width: '100%', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                  background: '#0a0807', color: C.text, border: `1px solid ${C.chipOffBd}`,
                  borderRadius: 8, padding: '11px 34px 11px 14px', fontSize: 14, fontWeight: 700,
                  fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                {MACK_BACKTEST_EPISODES.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
              </select>
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"
                   style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                   fill="none" stroke={C.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3.5 6 8 10.5 12.5 6" />
              </svg>
            </div>
          </div>

          <div>
            <div style={{ ...microLabel, marginBottom: 9 }}>Instrument</div>
            <div style={{ display: 'flex', gap: 7 }}>
              {episode.reads.map(r => (
                <Chip key={r.instrument} label={r.instrument}
                      active={r.instrument === instrument}
                      onClick={() => chooseInstrument(r.instrument)} />
              ))}
            </div>
          </div>

          <button
            onClick={() => setRevealed(v => !v)}
            aria-pressed={revealed}
            style={{
              fontSize: 13, fontFamily: 'inherit', padding: '9px 18px', borderRadius: 8,
              cursor: 'pointer', whiteSpace: 'nowrap',
              border: `1px solid ${revealed ? C.amber : C.chipOffBd}`,
              background: revealed ? C.chipBg : 'transparent',
              color: revealed ? C.amberBright : C.textSub,
              transition: 'all 0.15s',
            }}
          >
            {revealed ? 'Re-seal outcome' : 'Reveal outcome'}
          </button>
        </div>
      </div>

      {/* ── FLEET RECONSTRUCTION ─────────────────────────────────────── */}
      <div style={{ ...card, padding: '19px 24px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ ...panelTitle, flex: 1 }}>Fleet reconstruction — what INGEST pulled for that date</div>
          <span style={{ ...outlinePill(C.blue), flexShrink: 0 }}>As-released · {episode.landing_date}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 14, marginTop: 15 }}>
          {episode.reconstructed.map(v => (
            <div key={v.variable} style={{ ...card, padding: '13px 13px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...microLabel, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {v.variable}
                </span>
                <span
                  title={v.vintage_flag}
                  style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: v.vintage_flag === 'absent' ? C.red
                              : v.vintage_flag === 'as-released' ? C.textSub : C.textMuted,
                  }}
                />
              </div>
              <div style={{
                fontSize: 19, fontWeight: 700, marginTop: 7, letterSpacing: '-0.01em',
                color: v.vintage_flag === 'absent' ? C.textMuted : C.text,
              }}>{v.value}</div>
              <div style={{ marginTop: 9 }}>
                <span style={{
                  fontSize: 9.5, fontFamily: MONO, color: C.textSub,
                  border: `1px solid ${C.chipOffBd}`, borderRadius: 5, padding: '3px 7px', whiteSpace: 'nowrap',
                }}>{v.source}</span>
              </div>
              <div style={{
                fontSize: 9.5, fontFamily: MONO, marginTop: 10,
                color: v.vintage_flag === 'absent' ? C.red : C.amberDim,
              }}>
                · {v.pulled_at}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: 16, background: C.formulaBg, borderLeft: `3px solid ${C.amber}`,
          borderRadius: '3px 8px 8px 3px', padding: '13px 16px',
          fontSize: 12.5, lineHeight: 1.65, color: C.textMuted,
        }}>
          <span style={{ color: C.amberBright, fontWeight: 700 }}>Vintage discipline.</span>{' '}
          Every value is the print as it stood on the landing date, not as later revised. A framework
          backtested on revised data will always look better than it was — the single most common way
          a macro backtest lies. Any figure the fleet cannot recover as-released is emitted as absent
          rather than filled from the current series.
        </div>
      </div>

      {/* ── COMMITTED READ + SHEET STATE / CAPACITY ──────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.35fr', gap: 17, alignItems: 'start' }}>

        <div style={{ ...card, padding: '17px 20px 20px' }}>
          <div style={{ ...microLabel, marginBottom: 12 }}>Committed read — locked before reveal</div>
          <div style={{ background: C.formulaBg, border: `1px solid ${C.chipBorder}`, borderRadius: 10, padding: '17px 18px 18px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>
              {read.committed.tier} — {read.committed.sheet} resident
            </div>
            <div style={{ ...monoSmall, color: C.amber, marginTop: 7, fontWeight: 700 }}>
              {read.committed.confidence} confidence · committed {read.committed.committed_on}
            </div>
            <div style={{ ...monoSmall, color: C.textMuted, marginTop: 5 }}>
              {cap.band.label} · {read.committed.dominant_block} dominant
            </div>

            <div style={{ marginTop: 15 }}>
              {read.committed.ranked.map((r, n) => (
                <div key={r.tier} style={{
                  display: 'grid', gridTemplateColumns: '16px minmax(0,1fr) 75px 42px',
                  alignItems: 'center', gap: 9, padding: '9px 0',
                  borderTop: n === 0 ? 'none' : `1px solid ${C.rule}`,
                }}>
                  <span style={{ fontSize: 10, fontFamily: MONO, color: C.textMuted }}>{n + 1}</span>
                  <span style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>{r.tier}</span>
                  {/* Fill is proportional to this row's own share — see RANK_BAR_CEILING. */}
                  <div style={{ height: 4, borderRadius: 2, background: '#2c251e', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(1, r.pct / RANK_BAR_CEILING) * 100}%`, height: '100%',
                      background: `linear-gradient(90deg, #a9703a, ${C.amberBright})`, borderRadius: 2,
                    }} />
                  </div>
                  <span style={{ fontSize: 12, fontFamily: MONO, color: C.green, textAlign: 'right' }}>
                    {Math.round(r.pct * 100)}%
                  </span>
                </div>
              ))}
            </div>

            <div style={{ ...microLabel, marginTop: 16, marginBottom: 8 }}>Discriminator — sealed with the read</div>
            <div style={{ fontSize: 12.5, color: C.textSub, lineHeight: 1.6 }}>{read.committed.discriminator}</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 14, marginTop: 18 }}>
              <div>
                <div style={{ ...microLabel, marginBottom: 8 }}>Speed</div>
                <span style={{
                  fontSize: 9.5, fontFamily: MONO, letterSpacing: '0.12em', textTransform: 'uppercase',
                  border: `1px solid ${C.teal}`, color: C.teal, borderRadius: 20, padding: '4px 10px',
                }}>{read.committed.speed}</span>
              </div>
              <div>
                <div style={{ ...microLabel, marginBottom: 8 }}>Review at</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>{read.committed.review_point}</div>
              </div>
              <div>
                <div style={{ ...microLabel, marginBottom: 8 }}>Implied side</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.teal }}>{read.committed.implied_side}</div>
              </div>
            </div>

            <div style={{ height: 1, background: C.rule, margin: '17px 0 14px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 9, fontFamily: MONO, letterSpacing: '0.12em', textTransform: 'uppercase',
                border: `1px solid ${C.textMuted}`, color: C.textMuted, borderRadius: 20,
                padding: '4px 10px', whiteSpace: 'nowrap',
              }}>{read.committed.kind === 'new' ? 'New attribution' : 'Continuation'}</span>
              <span style={{ ...monoSmall, color: C.violet }}>{read.committed.cycle_line}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>
          <div style={{ ...card, padding: '17px 22px 20px', overflowX: 'auto' }}>
            <div style={{ minWidth: 700 }}>
              <div style={{ ...microLabel, marginBottom: 12 }}>Sheet state on the landing date</div>
              <div style={{ ...STATE_GRID, padding: '0 0 10px', borderBottom: `1px solid ${C.rule}` }}>
                <span style={microLabel}>Sheet</span>
                <span style={microLabel}>Phase</span>
                {STATE_BLOCKS.map(b => R(b, microLabel, `h-${b}`))}
                {R('A', microLabel)}
                <span style={{ ...microLabel, paddingLeft: 12 }}>State</span>
              </div>
              {episode.sheet_state.map(s => {
                const phase = classifyStage(s.delta_growth, s.delta_inflation);
                return (
                  <div key={s.sheet} style={{ ...STATE_GRID, minHeight: 42, borderBottom: `1px solid ${C.rule}` }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{s.sheet}</span>
                    <span style={{ fontSize: 12.5, color: PHASE_COLOUR[phase] ?? C.textSub }}>{phase}</span>
                    {STATE_BLOCKS.map(b => R(
                      (s.blocks[b] ?? 0).toFixed(2),
                      { ...monoSmall, color: blockBand(s.blocks[b] ?? 0) }, `${s.sheet}-${b}`,
                    ))}
                    {R(s.activation.toFixed(2), { ...monoSmall, color: activationBand(s.activation) })}
                    <span style={{ paddingLeft: 12 }}>{statePill(s.reactive)}</span>
                  </div>
                );
              })}
              <div style={{ fontSize: 11.5, fontStyle: 'italic', color: C.textMuted, marginTop: 14, lineHeight: 1.65 }}>
                The chain runs on these values, not on today's. Activation is recomputed from the
                calendar as it stood — the schedule for any past date is a matter of record.
              </div>
            </div>
          </div>

          <div style={{ ...card, padding: '17px 22px 20px', overflowX: 'auto' }}>
            <div style={{ minWidth: 700 }}>
              <div style={{ ...microLabel, marginBottom: 12 }}>Capacity on the landing date</div>
              <div style={{ ...CAPACITY_GRID, padding: '0 0 10px', borderBottom: `1px solid ${C.rule}` }}>
                <span style={microLabel}>Instrument</span>
                {R('Price then', microLabel)}
                {R('Σ then', microLabel)}
                {R('Ceiling₁', microLabel)}
                <span style={{ ...microLabel, paddingLeft: 16 }}>Band</span>
                {R('R', microLabel)}
              </div>
              <div style={{ ...CAPACITY_GRID, minHeight: 42 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{read.capacity_then.instrument}</span>
                {R(read.capacity_then.price_then, { ...monoSmall, color: C.amber })}
                {R(`${read.capacity_then.sigma_then.toFixed(2)}%`, { ...monoSmall, color: C.textSub })}
                {R(`${cap.ceiling_1.toFixed(2)}%`, { ...monoSmall, color: C.amber })}
                <span style={{ fontSize: 12.5, color: C.textMuted, paddingLeft: 16 }}>{cap.band.label}</span>
                {R(String(cap.band.R), { ...monoSmall, color: C.violet, fontWeight: 700 })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── OUTCOME — SEALED BY DEFAULT ──────────────────────────────── */}
      <div style={{ ...card, padding: '19px 24px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ ...panelTitle, flex: 1 }}>Outcome at declared horizon</div>
          <span style={{ ...outlinePill(revealed ? C.green : C.textMuted), flexShrink: 0 }}>
            {revealed ? 'Revealed' : 'Sealed'}
          </span>
        </div>

        {!revealed ? (
          <div style={{ ...panelNote, maxWidth: 1180, marginTop: 10 }}>
            Outcome is sealed until you reveal it. The point of blinding is that the read above was
            assembled strictly from data available on the landing date, and committed with its
            discriminator and horizon before anything downstream was known. Study the committed read
            first — decide whether you would have taken it — then reveal.
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 14, marginTop: 15 }}>
              <div style={{ ...card, padding: '13px 15px 15px' }}>
                <div style={microLabel}>Price at horizon</div>
                <div style={{ fontSize: 19, fontWeight: 700, color: C.text, marginTop: 7 }}>{read.outcome.price_at_horizon}</div>
              </div>
              <div style={{ ...card, padding: '13px 15px 15px' }}>
                <div style={microLabel}>Move</div>
                <div style={{ fontSize: 19, fontWeight: 700, marginTop: 7, fontFamily: MONO,
                              color: read.outcome.move.startsWith('−') ? C.red : C.green }}>
                  {read.outcome.move}
                </div>
              </div>
              <div style={{ ...card, padding: '13px 15px 15px' }}>
                <div style={microLabel}>Ran</div>
                <div style={{ fontSize: 19, fontWeight: 700, color: C.text, marginTop: 7 }}>{read.outcome.ran}</div>
              </div>
              <div style={{ ...card, padding: '13px 15px 15px' }}>
                <div style={microLabel}>Attribution</div>
                <div style={{ fontSize: 19, fontWeight: 700, marginTop: 7,
                              color: read.outcome.attribution_held ? C.green : C.red }}>
                  {read.outcome.attribution_held ? 'Held' : 'Failed'}
                  <span style={{ fontSize: 12, fontFamily: MONO, color: C.textMuted, marginLeft: 8 }}>
                    dir {read.outcome.direction_held ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            </div>
            <div style={{
              marginTop: 15, background: C.formulaBg,
              borderLeft: `3px solid ${read.outcome.attribution_held ? C.green : C.red}`,
              borderRadius: '3px 8px 8px 3px', padding: '13px 16px',
              fontSize: 12.5, lineHeight: 1.65, color: C.textMuted,
            }}>
              <span style={{ color: read.outcome.attribution_held ? C.green : C.red, fontWeight: 700 }}>
                {read.outcome.attribution_held ? 'What happened.' : 'What happened — and where it broke.'}
              </span>{' '}
              {read.outcome.note}
            </div>
          </>
        )}
      </div>

      {/* ── MANUAL VERIFICATION + RUNNING TALLY ──────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr', gap: 17, alignItems: 'start' }}>

        <div style={{ ...card, padding: '19px 24px 20px' }}>
          <div style={panelTitle}>Manual verification — what to check on the chart</div>
          <div style={{ ...panelNote, maxWidth: 620 }}>
            The framework claims flow character and persistence, not a price target. These are the
            specific, checkable statements it made on the landing date.
          </div>
          <div style={{ marginTop: 14 }}>
            {read.checks.map((k, n) => (
              <div key={k.claim} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '13px 0', borderTop: n === 0 ? 'none' : `1px solid ${C.rule}`,
              }}>
                <span style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.5 }}>·</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{k.claim}</div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Claimed: {k.claimed}</div>
                  {revealed && (
                    <div style={{ fontSize: 12, color: C.textSub, marginTop: 3 }}>Actual: {k.actual}</div>
                  )}
                </div>
                {/* Marks appear only once revealed — the mark IS the outcome. */}
                <span style={{
                  ...monoSmall, flexShrink: 0, whiteSpace: 'nowrap',
                  color: !revealed ? C.textMuted : k.result === 'hit' ? C.green : C.red,
                }}>
                  {!revealed ? 'sealed' : k.result === 'hit' ? 'hit ✓' : 'miss ✗'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...card, padding: '19px 24px 20px', overflowX: 'auto' }}>
          <div style={{ minWidth: 640 }}>
            <div style={panelTitle}>Running tally — reconstructed episodes</div>

            <div style={{ ...TALLY_GRID, padding: '15px 0 10px', borderBottom: `1px solid ${C.rule}` }}>
              <span style={microLabel}>Episode</span>
              <span style={microLabel}>Instrument</span>
              <span style={microLabel}>Attribution</span>
              <span style={microLabel}>Kind</span>
              {R('Ran', microLabel)}
              <span style={{ ...microLabel, paddingLeft: 14 }}>Held</span>
            </div>
            {MACK_BACKTEST_TALLY.map((t, n) => (
              <div key={`${t.episode}-${t.instrument}-${n}`} style={{ ...TALLY_GRID, minHeight: 42, borderBottom: `1px solid ${C.rule}` }}>
                <span style={{ fontSize: 12.5, color: C.textSub }}>{t.episode}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{t.instrument}</span>
                <span style={{ fontSize: 12.5, color: C.textSub }}>{t.attribution}</span>
                <span style={{ ...monoSmall, color: t.kind === 'new' ? C.violet : C.textMuted }}>{t.kind}</span>
                {R(t.ran, { ...monoSmall, color: C.textSub })}
                <span style={{ fontSize: 12.5, paddingLeft: 14, whiteSpace: 'nowrap' }}>
                  <span style={{ color: t.held ? C.green : C.red, fontWeight: 700 }}>{t.held ? 'Held' : 'Failed'}</span>
                  <span style={{ color: C.textMuted }}> · dir </span>
                  <span style={{ color: t.direction_held ? C.text : C.red }}>{t.direction_held ? '✓' : '✗'}</span>
                </span>
              </div>
            ))}

            {/* THE METRICS READ UNMEASURED, DELIBERATELY. A hit rate from a
                handful of episodes is an anecdote with decimals. Only the COUNTS
                are shown, and they distinguish total reads from independent ones. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 16, marginTop: 18 }}>
              <div>
                <div style={microLabel}>Reads run</div>
                <div style={{ marginTop: 7 }}>
                  <span style={{ fontSize: 19, fontWeight: 700, fontFamily: MONO, color: C.amber }}>{readsRun}</span>
                  <span style={{ ...monoSmall, color: C.textMuted }}> · </span>
                  <span style={{ ...monoSmall, color: C.violet }}>{independent} independent</span>
                </div>
              </div>
              <div>
                <div style={microLabel}>Attribution held</div>
                <div style={{ fontSize: 15, fontFamily: MONO, color: C.textSub, marginTop: 7 }}>insufficient base</div>
              </div>
              <div>
                <div style={microLabel}>Persistence vs declared</div>
                <div style={{ fontSize: 15, fontFamily: MONO, color: C.textSub, marginTop: 7 }}>insufficient base</div>
              </div>
            </div>

            <div style={{
              marginTop: 16, background: C.formulaBg, borderLeft: `3px solid ${C.teal}`,
              borderRadius: '3px 8px 8px 3px', padding: '13px 16px',
              fontSize: 12, lineHeight: 1.65, color: C.textMuted,
            }}>
              <span style={{ color: C.teal, fontWeight: 700 }}>Only new attributions count.</span>{' '}
              A read that persists across an unchanged cycle is one call re-confirmed, not several.
              Continuations are logged for the arc and excluded from the hit rate — counting them
              would inflate the sample by the length of the trend. Persistence against declared
              review point is tracked separately, and is arguably the better test: if slow-tier reads
              routinely run twenty weeks against a declared eight, the horizon table is wrong.
            </div>

            <div style={{
              marginTop: 10, background: C.formulaBg, borderLeft: `3px solid ${C.violet}`,
              borderRadius: '3px 8px 8px 3px', padding: '13px 16px',
              fontSize: 12, lineHeight: 1.65, color: C.textMuted,
            }}>
              <span style={{ color: C.violet, fontWeight: 700 }}>Reconstructed is not lived.</span>{' '}
              Both classes seed the precedent tier, but a call committed before the outcome existed in
              real time is stronger evidence than one assembled from an archive — even blinded. The
              gap between how well each predicts is itself measurable, and if they diverge the
              reconstruction method has a bias to correct.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
